import { useCallback, useEffect, useState } from 'react';
import SettingsBreadcrumb from '@/pages/settings/components/SettingsBreadcrumb';
import {
  runBackupCheck,
  runDatabaseChecks,
  runDeviceChecks,
  runErrorCheck,
  runStorageCheck,
  worstStatus,
  type CheckStatus,
  type DiagnosticCheck,
} from '@/lib/diagnostics';

const STATUS_STYLE: Record<CheckStatus, { dot: string; text: string; label: string }> = {
  ok: { dot: 'bg-accent-500', text: 'text-accent-700', label: 'Correcto' },
  warn: { dot: 'bg-secondary-500', text: 'text-secondary-800', label: 'Atención' },
  error: { dot: 'bg-primary-500', text: 'text-primary-700', label: 'Problema' },
  unknown: { dot: 'bg-foreground-400', text: 'text-foreground-600', label: 'Sin dato' },
};

function CheckRow({ check }: { check: DiagnosticCheck }) {
  const style = STATUS_STYLE[check.status];
  return (
    <div className="flex items-start gap-3 px-4 py-3 border-b border-background-200 last:border-b-0">
      <span className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${style.dot}`} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <p className="text-sm font-semibold text-foreground-950">{check.label}</p>
          <span className={`text-xs font-semibold ${style.text}`}>{check.value}</span>
        </div>
        <p className="mt-1 text-xs text-foreground-600 leading-relaxed">{check.detail}</p>
      </div>
    </div>
  );
}

function SectionCard({
  title,
  icon,
  description,
  checks,
}: {
  title: string;
  icon: string;
  description: string;
  checks: DiagnosticCheck[];
}) {
  return (
    <section className="mt-5 rounded-lg border border-background-200 bg-background-50 overflow-hidden">
      <header className="px-4 py-3.5 border-b border-background-200 bg-background-100/60">
        <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
          <span className="w-7 h-7 rounded-md bg-background-50 border border-background-200 flex items-center justify-center text-primary-700">
            <i className={`${icon} text-base`} />
          </span>
          {title}
        </h2>
        <p className="mt-1.5 text-xs text-foreground-600">{description}</p>
      </header>
      {checks.length === 0 ? (
        <p className="px-4 py-6 text-sm text-foreground-600">Sin datos disponibles.</p>
      ) : (
        checks.map((c) => <CheckRow key={c.id} check={c} />)
      )}
    </section>
  );
}

export default function DiagnosticsPage() {
  const [dbChecks, setDbChecks] = useState<DiagnosticCheck[]>([]);
  const [opsChecks, setOpsChecks] = useState<DiagnosticCheck[]>([]);
  const [deviceChecks, setDeviceChecks] = useState<DiagnosticCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorCheck, setErrorCheck] = useState<DiagnosticCheck | null>(null);
  const [lastRun, setLastRun] = useState<string | null>(null);

  const run = useCallback(async () => {
    setLoading(true);
    setDeviceChecks(runDeviceChecks());
    try {
      const [db, errors, backup, storage] = await Promise.all([
        runDatabaseChecks(),
        runErrorCheck(7),
        runBackupCheck(),
        runStorageCheck(),
      ]);
      setDbChecks(db);
      setErrorCheck(errors.check);
      setOpsChecks([errors.check, backup, storage]);
    } catch {
      setDbChecks([]);
      setOpsChecks([]);
    } finally {
      setLastRun(new Date().toLocaleString('es-DO'));
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void run();
  }, [run]);

  const all = [...dbChecks, ...opsChecks, ...deviceChecks];
  const overall = worstStatus(all);
  const overallStyle = STATUS_STYLE[overall];
  const problems = all.filter((c) => c.status === 'error' || c.status === 'warn');

  return (
    <div className="max-w-4xl">
      <SettingsBreadcrumb label="Diagnóstico" icon="ri-pulse-line" />
      <h1 className="mt-2 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
        Estado del sistema
      </h1>
      <p className="mt-2 text-foreground-600">
        Comprobación del tiempo de respuesta de la base de datos, errores recientes, copias de
        seguridad y salud del equipo donde corre el POS.
      </p>

      <div className="mt-5 rounded-lg border border-background-200 bg-background-100/60 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className={`w-9 h-9 rounded-full flex items-center justify-center ${overallStyle.dot}`}>
            <i
              className={`text-lg text-background-50 ${
                overall === 'ok'
                  ? 'ri-checkbox-circle-line'
                  : overall === 'error'
                    ? 'ri-error-warning-line'
                    : 'ri-alert-line'
              }`}
            />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground-950">
              {loading ? 'Analizando...' : `Resultado general: ${overallStyle.label}`}
            </p>
            <p className="text-xs text-foreground-600">
              {lastRun ? `Última comprobación: ${lastRun}` : 'Sin comprobaciones aún'}
            </p>
          </div>
        </div>
        <button
          onClick={() => void run()}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
        >
          <i className={loading ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'} />
          {loading ? 'Comprobando...' : 'Volver a comprobar'}
        </button>
      </div>

      {!loading && problems.length > 0 && (
        <div className="mt-4 rounded-lg border border-secondary-200 bg-secondary-50 px-4 py-3">
          <p className="text-sm font-semibold text-foreground-950 flex items-center gap-2">
            <i className="ri-lightbulb-line text-secondary-700" /> Puntos a revisar
          </p>
          <ul className="mt-2 space-y-1.5 list-disc pl-5 text-xs text-foreground-700">
            {problems.slice(0, 6).map((p) => (
              <li key={`rec-${p.id}`}>
                <span className="font-medium">{p.label}:</span> {p.detail}
              </li>
            ))}
          </ul>
        </div>
      )}

      <SectionCard
        title="Base de datos"
        icon="ri-database-2-line"
        description="Tiempo de respuesta de las tablas que más usa el POS. Arriba de 900 ms indica consultas pesadas o falta de índices."
        checks={loading ? [] : dbChecks}
      />

      <SectionCard
        title="Operación y respaldos"
        icon="ri-shield-check-line"
        description="Errores registrados en la bitácora, última copia de seguridad y disponibilidad del almacenamiento de imágenes."
        checks={loading ? [] : opsChecks}
      />

      <SectionCard
        title="Este equipo"
        icon="ri-computer-line"
        description="Estado de la red, datos locales del navegador y consumo de memoria de la pestaña del POS."
        checks={deviceChecks}
      />

      <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-4">
        <h2 className="font-heading text-base font-semibold text-foreground-950 flex items-center gap-2">
          <i className="ri-information-line text-foreground-600" /> Alcance de esta comprobación
        </h2>
        <p className="mt-2 text-xs text-foreground-600 leading-relaxed">
          Este panel mide lo que ocurre desde el navegador hacia tu base de datos y servicios. No
          puede leer los registros del servidor de alojamiento, del sistema operativo ni del
          proveedor de base de datos. Los respaldos automáticos y la verificación de restauración
          se configuran en el panel de Supabase y en el alojamiento, no desde aquí.
        </p>
        {errorCheck && errorCheck.status === 'unknown' && (
          <p className="mt-2 text-xs text-foreground-500">
            La bitácora de actividad no es legible con tu usuario actual; los contadores de errores
            aparecerán vacíos.
          </p>
        )}
      </div>
    </div>
  );
}